import { Controller, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { signUp } from 'aws-amplify/auth'
import * as yup from 'yup'

import { LabelInput } from '@/common/ui/LabelInput'
import { type SignupForm as SignupFormT } from '@/common/types'
import { Button } from '@/common/ui/button'
import { Checkbox } from '@/common/ui/checkbox'
import { SocialSignIn } from './socialSignIn'
import { toast } from 'sonner'
import { Fragment, useState } from 'react'
import { InputError } from '@/common/ui/InputError'
import { PasswordInput } from '@/common/ui/PasswordInput'
import { OrDivider } from './OrDivider'
import { createSearchParams, useNavigate } from 'react-router-dom'
import { APP_ROUTES, USAGE_POLICIES } from '@/common/constants'
import { Transition } from '@headlessui/react'
import { ONBOARDING_COPY } from '@/locales/en'
import { useFetchUsagePolicy } from '@/user/userQueries'
import { SIX_PENDING_SIGNUP_TERMS_VERSION_ID } from '@/common/storage/keys'

const schema = yup
    .object({
        email: yup.string().email('Please enter valid email').required(),
        password: yup
            .string()
            .min(8, 'Password should be at least 8 characters long')
            .matches(/^.*[a-z]+.*$/, 'Must contain at least 1 lower case letter')
            .matches(/^.*[A-Z]+.*$/, 'Must contain at least 1 upper case letter')
            .matches(/^.*[0-9]+.*$/, 'Must contain at least 1 number')
            .max(50, 'Password can not be longer than 50 characters')
            .required(),
        confirmPassword: yup.string().oneOf([yup.ref('password')], 'Passwords must match'),
        tnc: yup.boolean().oneOf([true], 'Please accept the terms and conditions'),
    })
    .required()

export function CreateAccountTab() {
    const [isRunning, setIsRunning] = useState(false)
    const [isTncAccepted, setIsTncAccepted] = useState(false)
    const [signupWithEmail, setSignupWithEmail] = useState(false)
    const { data: signupTerms } = useFetchUsagePolicy(USAGE_POLICIES.SIGNUP_TERMS)
    const navigate = useNavigate()
    const {
        register,
        handleSubmit,
        control,
        setValue,
        trigger,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        mode: 'onBlur',
    })

    const persistSignupTermsIntent = (checked: boolean) => {
        if (checked && signupTerms?.versionId) {
            sessionStorage.setItem(SIX_PENDING_SIGNUP_TERMS_VERSION_ID, signupTerms.versionId)
            return
        }

        sessionStorage.removeItem(SIX_PENDING_SIGNUP_TERMS_VERSION_ID)
    }

    const onSubmit = async (data: SignupFormT) => {
        if (!signupTerms?.versionId) {
            toast.error('Terms are still loading. Please wait a moment and try again.')
            return
        }

        const { email, password } = data
        persistSignupTermsIntent(true)

        try {
            setIsRunning(true)
            const { nextStep } = await signUp({
                username: email,
                password,
                options: {
                    userAttributes: {
                        email,
                    },
                    autoSignIn: true,
                },
            })
            if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
                navigate({
                    pathname: APP_ROUTES.CONFIRM_USER,
                    search: createSearchParams({ username: email }).toString(),
                })
            }
        } catch (error: any) {
            if (error.message.includes('already exists')) {
                toast.error('Email already exists', {
                    id: 'email-exists',
                })
                return
            }
            toast.error(error.message)
        } finally {
            setIsRunning(false)
        }
    }

    return (
        <>
            <form noValidate className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
                <div>
                    <div className="flex gap-3">
                        <Controller
                            name="tnc"
                            control={control}
                            defaultValue={false}
                            render={({ field }) => {
                                // @ts-ignore
                                const onChange = (checked) => {
                                    field.onChange()

                                    setIsTncAccepted(checked as boolean)
                                    persistSignupTermsIntent(checked as boolean)
                                    setValue('tnc', checked as boolean)
                                    trigger('tnc')
                                }
                                return <Checkbox onCheckedChange={onChange} {...register('tnc')} id="tnc" />
                            }}
                        />
                        <label className="text-sm" htmlFor="tnc">
                            {signupTerms?.disclaimer && (
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: signupTerms.disclaimer.replace('Privacy Policy', 'Privacy&nbsp;Policy'),
                                    }}
                                />
                            )}
                        </label>
                    </div>
                    <InputError message={errors.tnc?.message} />
                </div>
                <SocialSignIn isTncAccepted={isTncAccepted} onClick={() => trigger('tnc')} />
                <div className="relative min-h-[56px] w-full">
                    <Transition
                        show={!signupWithEmail}
                        enter="transition-opacity duration-200"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="transition-opacity duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        {!signupWithEmail ? (
                            <Button variant="outline" className="sm-4 w-full" onClick={() => setSignupWithEmail(true)}>
                                {ONBOARDING_COPY.login.emailSignup}
                            </Button>
                        ) : (
                            <Fragment />
                        )}
                    </Transition>

                    <Transition
                        show={signupWithEmail}
                        enter="transition-all duration-300"
                        enterFrom="opacity-0 max-h-0 -translate-y-2"
                        enterTo="opacity-100 max-h-[500px] translate-y-0"
                        leave="transition-all duration-200"
                        leaveFrom="opacity-100 max-h-[500px] translate-y-0"
                        leaveTo="opacity-0 max-h-0 -translate-y-2"
                    >
                        <div className="fex flex-col gap-4 space-y-3">
                            <OrDivider />
                            <LabelInput
                                {...register('email')}
                                error={errors.email ? 'Please enter a valid email address' : undefined}
                                required
                                type="email"
                                id="email"
                                label={ONBOARDING_COPY.login.email}
                            />
                            <PasswordInput
                                {...register('password')}
                                error={errors.password?.message}
                                required
                                id="password"
                                label={ONBOARDING_COPY.login.password}
                            />
                            <PasswordInput
                                {...register('confirmPassword')}
                                error={errors.confirmPassword?.message}
                                required
                                id="confirm-password"
                                label={ONBOARDING_COPY.login.confirmPassword}
                            />
                            <Button
                                loading={isRunning}
                                variant="secondary"
                                type="submit"
                                className="sm:w-max mt-2 w-full"
                            >
                                {ONBOARDING_COPY.login.createAccount}
                            </Button>
                        </div>
                    </Transition>
                </div>
            </form>
        </>
    )
}
